import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, FileArchive, Loader2, ShieldAlert } from "lucide-react";

import {
    commitScorm,
    finishScorm,
    getScormState,
    initializeScorm,
    launchScormContentApi,
    setScormValues,
} from "../../../../services/moduleScormApi";
import { installScormApiBridge } from "./ScormApiBridge";
import ScormErrorBoundary from "./ScormErrorBoundary";
import ScormLaunchFrame from "./ScormLaunchFrame";

const extractErrorMessage = (error) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || "Impossible de lancer ce package SCORM.";

export default function ScormPlayer({ moduleId, content, nodeRequest, onNavigationChange, onContextChange }) {
    const [launchState, setLaunchState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [frameLoading, setFrameLoading] = useState(false);
    const [error, setError] = useState(null);
    const [bridgeReady, setBridgeReady] = useState(false);
    const [runtimeState, setRuntimeState] = useState(null);
    const [saveState, setSaveState] = useState("idle");
    const [diagnostics, setDiagnostics] = useState(null);
    const initialLaunchKeyRef = useRef(null);
    const handledNodeRequestRef = useRef(null);
    const saveStateTimerRef = useRef(null);
    const sequencingRefreshTimersRef = useRef([]);
    const sequencingRefreshKeyRef = useRef(null);

    const launch = useCallback(
        async (target = null) => {
            if (!moduleId || !content?.id) return;
            setLoading(true);
            setFrameLoading(true);
            setBridgeReady(false);
            setDiagnostics(null);
            setError(null);
            try {
                const scoId = target && typeof target === "object" ? target.scoId || null : target;
                const nodeId = target && typeof target === "object" ? target.nodeId || null : null;
                const response = await launchScormContentApi(moduleId, content.id, scoId, nodeId);
                setLaunchState(response.data);
                setRuntimeState(buildRuntimeStateFromLaunch(response.data));
            } catch (err) {
                setLaunchState(null);
                setRuntimeState(null);
                setFrameLoading(false);
                setError(extractErrorMessage(err));
            } finally {
                setLoading(false);
            }
        },
        [content?.id, moduleId]
    );

    useEffect(() => {
        const launchKey = `${moduleId || ""}-${content?.id || ""}`;
        if (initialLaunchKeyRef.current === launchKey) return;
        initialLaunchKeyRef.current = launchKey;
        launch(null);
    }, [content?.id, launch, moduleId]);

    useEffect(() => {
        const requestId = nodeRequest?.requestId;
        const nodeId = nodeRequest?.nodeId;
        const scoId = nodeRequest?.scoId;
        if (!requestId || (!nodeId && !scoId) || handledNodeRequestRef.current === requestId) return;

        handledNodeRequestRef.current = requestId;
        if (nodeId && String(nodeId) === String(launchState?.nodeId)) return;
        launch({ nodeId, scoId });
    }, [launch, launchState?.nodeId, nodeRequest]);

    useEffect(() => {
        if (!onNavigationChange) return undefined;

        if (!launchState?.packageId) {
            onNavigationChange(null);
            return undefined;
        }

        onNavigationChange({
            contentId: content.id,
            packageId: launchState.packageId,
            scoId: launchState.scoId,
            currentNodeId: launchState.nodeId,
            previousScoId: launchState.previousScoId,
            nextScoId: launchState.nextScoId,
            previousNodeId: launchState.previousNodeId,
            nextNodeId: launchState.nextNodeId,
            tree: launchState.tree,
            loading,
        });

        return undefined;
    }, [
        content.id,
        launchState?.nextNodeId,
        launchState?.nextScoId,
        launchState?.nodeId,
        launchState?.packageId,
        launchState?.previousNodeId,
        launchState?.previousScoId,
        launchState?.scoId,
        launchState?.tree,
        loading,
        onNavigationChange,
    ]);

    useEffect(() => {
        if (!onContextChange || !launchState?.nodeId) return;
        onContextChange({
            contentTypeContext: "SCORM",
            scormNodeId: launchState.nodeId,
            scormNodeTitle: findScormNodeTitle(launchState.tree?.nodes || [], launchState.nodeId) || launchState.scoTitle || content.title,
        });
    }, [content.title, launchState?.nodeId, launchState?.scoTitle, launchState?.tree, onContextChange]);

    useEffect(() => () => {
        if (onNavigationChange) {
            onNavigationChange(null);
        }
    }, [onNavigationChange]);

    useEffect(() => () => {
        if (saveStateTimerRef.current) {
            window.clearTimeout(saveStateTimerRef.current);
        }
        sequencingRefreshTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        sequencingRefreshTimersRef.current = [];
    }, []);

    const handleRuntimeStateChange = useCallback((state) => {
        setRuntimeState(state);
        setLaunchState((current) => {
            if (!current || !state) return current;
            return {
                ...current,
                tree: state.tree || current.tree,
                nodeId: state.nodeId || current.nodeId,
                previousScoId: state.previousScoId ?? current.previousScoId,
                nextScoId: state.nextScoId ?? current.nextScoId,
                previousNodeId: state.previousNodeId ?? current.previousNodeId,
                nextNodeId: state.nextNodeId ?? current.nextNodeId,
                registrationStatus: state.registrationStatus || current.registrationStatus,
                initializedStatus: state.attemptStatus || current.initializedStatus,
                completionPercent: state.completionPercent ?? current.completionPercent,
                lessonStatus: state.lessonStatus ?? current.lessonStatus,
                completionStatus: state.completionStatus ?? current.completionStatus,
                successStatus: state.successStatus ?? current.successStatus,
                scoreRaw: state.scoreRaw ?? current.scoreRaw,
                scoreScaled: state.scoreScaled ?? current.scoreScaled,
                totalTimeSeconds: state.totalTimeSeconds ?? current.totalTimeSeconds,
            };
        });
    }, []);

    const refreshSequencingState = useCallback(async (stateHint = null) => {
        const attemptId = stateHint?.attemptId || launchState?.attemptId;
        const registrationId = stateHint?.registrationId || launchState?.registrationId;
        if (!attemptId || !registrationId || !moduleId || !content?.id) return;
        try {
            const response = await getScormState(moduleId, content.id, attemptId, registrationId);
            handleRuntimeStateChange(response.data);
        } catch (err) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("[SCORM API] Sequencing refresh failed", err);
            }
        }
    }, [content?.id, handleRuntimeStateChange, launchState?.attemptId, launchState?.registrationId, moduleId]);

    const scheduleSequencingRefresh = useCallback((stateHint = null) => {
        const attemptId = stateHint?.attemptId || launchState?.attemptId;
        const registrationId = stateHint?.registrationId || launchState?.registrationId;
        if (!attemptId || !registrationId) return;
        const refreshKey = `${attemptId}-${stateHint?.lastCommitAt || stateHint?.completionStatus || stateHint?.lessonStatus || stateHint?.successStatus || Date.now()}`;
        if (sequencingRefreshKeyRef.current === refreshKey) return;
        sequencingRefreshKeyRef.current = refreshKey;
        sequencingRefreshTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        sequencingRefreshTimersRef.current = [300, 800].map((delay) => window.setTimeout(() => {
            refreshSequencingState(stateHint);
        }, delay));
    }, [launchState?.attemptId, launchState?.registrationId, refreshSequencingState]);

    const handleSaveStateChange = useCallback((state) => {
        setSaveState(state);
        if (saveStateTimerRef.current) {
            window.clearTimeout(saveStateTimerRef.current);
        }
        if (state === "saved") {
            saveStateTimerRef.current = window.setTimeout(() => setSaveState("idle"), 2500);
        }
    }, []);

    const handleApiCall = useCallback((event) => {
        setDiagnostics((current) => ({
            ...(current || {}),
            lastApiCall: event,
        }));
    }, []);

    const handleFrameDiagnostics = useCallback((event) => {
        setDiagnostics((current) => ({
            ...(current || {}),
            ...(event || {}),
        }));
    }, []);

    const goToPrevious = useCallback(() => {
        if (launchState?.previousNodeId) {
            launch({ nodeId: launchState.previousNodeId });
        } else if (launchState?.previousScoId) {
            launch({ scoId: launchState.previousScoId });
        }
    }, [launch, launchState?.previousNodeId, launchState?.previousScoId]);

    const goToNext = useCallback(() => {
        if (launchState?.nextNodeId) {
            launch({ nodeId: launchState.nextNodeId });
        } else if (launchState?.nextScoId) {
            launch({ scoId: launchState.nextScoId });
        }
    }, [launch, launchState?.nextNodeId, launchState?.nextScoId]);

    useEffect(() => {
        if (!launchState?.attemptId || !launchState?.registrationId) {
            setBridgeReady(false);
            return undefined;
        }

        const uninstall = installScormApiBridge({
            moduleId,
            contentId: content.id,
            packageId: launchState.packageId,
            scoId: launchState.scoId,
            registrationId: launchState.registrationId,
            attemptId: launchState.attemptId,
            version: launchState.version,
            runtimeVersion: launchState.runtimeVersion,
            launchUrl: launchState.launchUrl,
            nodeId: launchState.nodeId,
            runtimeValues: launchState.runtimeValues,
            initializedStatus: launchState.initializedStatus,
            onRuntimeStateChange: handleRuntimeStateChange,
            onSequencingRefresh: scheduleSequencingRefresh,
            onSaveState: handleSaveStateChange,
            onApiCall: handleApiCall,
            callbacks: {
                initialize: (payload) => initializeScorm(moduleId, content.id, payload),
                setValues: (payload) => setScormValues(moduleId, content.id, payload),
                commit: (payload) => commitScorm(moduleId, content.id, payload),
                finish: (payload) => finishScorm(moduleId, content.id, payload),
            },
        });
        setBridgeReady(true);
        return () => {
            uninstall();
            setBridgeReady(false);
        };
    }, [
        content.id,
        launchState?.attemptId,
        launchState?.initializedStatus,
        launchState?.launchUrl,
        launchState?.nodeId,
        launchState?.packageId,
        launchState?.registrationId,
        launchState?.runtimeValues,
        launchState?.runtimeVersion,
        launchState?.scoId,
        launchState?.version,
        handleRuntimeStateChange,
        scheduleSequencingRefresh,
        handleSaveStateChange,
        handleApiCall,
        moduleId,
    ]);

    if (loading && !launchState) {
        return (
            <div className="scorm-message">
                <Loader2 size={20} className="scorm-spin" />
                <strong>Preparation du package SCORM...</strong>
            </div>
        );
    }

    if (error) {
        return (
            <div className="scorm-message scorm-message--error">
                <ShieldAlert size={20} />
                <div>
                    <strong>SCORM indisponible</strong>
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (!launchState?.launchUrl) {
        return (
            <div className="scorm-message">
                <FileArchive size={20} />
                <strong>Aucun package SCORM pret a lancer.</strong>
            </div>
        );
    }

    return (
        <ScormErrorBoundary>
            <div className="scorm-player">
                <div className="scorm-player__header">
                    <div>
                        {/* <span className="scorm-version">{launchState.version}</span> */}
                        <strong>{content.title}</strong>
                    </div>
                    <div className="scorm-player__tools">
                        {frameLoading ? <span className="scorm-loading">Chargement du SCO...</span> : null}
                        <div className="scorm-inline-nav" aria-label="Navigation SCORM">
                            <button
                                type="button"
                                className="cr-btn cr-btn--secondary"
                                onClick={goToPrevious}
                                disabled={loading || (!launchState.previousNodeId && !launchState.previousScoId)}
                                title="Page SCORM precedente"
                            >
                                <ChevronLeft size={16} />
                                Precedent
                            </button>
                            <button
                                type="button"
                                className="cr-btn cr-btn--secondary"
                                onClick={goToNext}
                                disabled={loading || (!launchState.nextNodeId && !launchState.nextScoId)}
                                title="Page SCORM suivante"
                            >
                                Suivant
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <ScormProgressSummary
                    launchState={launchState}
                    runtimeState={runtimeState}
                    saveState={saveState}
                />

                <ScormRuntimeDiagnostics
                    bridgeReady={bridgeReady}
                    diagnostics={diagnostics}
                />

                <div className="scorm-stage">
                    {bridgeReady ? (
                        <ScormLaunchFrame
                            key={`${launchState.packageId}-${launchState.scoId}-${launchState.nodeId || "sco"}-${launchState.attemptId}-${launchState.launchUrl}`}
                            launchUrl={launchState.launchUrl}
                            title={content.title}
                            onLoad={() => setFrameLoading(false)}
                            onDiagnostics={handleFrameDiagnostics}
                        />
                    ) : (
                        <div className="scorm-message">
                            <Loader2 size={20} className="scorm-spin" />
                            <strong>Initialisation du runtime SCORM...</strong>
                        </div>
                    )}
                </div>
            </div>
        </ScormErrorBoundary>
    );
}

function ScormRuntimeDiagnostics({ bridgeReady, diagnostics }) {
    return null; // Hidden as per urgent UI request
}

const buildRuntimeStateFromLaunch = (launchState) => {
    if (!launchState) return null;
    return {
        registrationStatus: launchState.registrationStatus,
        attemptStatus: launchState.initializedStatus,
        completionPercent: launchState.completionPercent,
        lessonStatus: launchState.lessonStatus,
        completionStatus: launchState.completionStatus,
        successStatus: launchState.successStatus,
        scoreRaw: launchState.scoreRaw,
        scoreScaled: launchState.scoreScaled,
        totalTimeSeconds: launchState.totalTimeSeconds,
        lastCommitAt: launchState.lastAccessAt,
    };
};

const findScormNodeTitle = (nodes = [], nodeId) => {
    for (const node of nodes) {
        if (String(node.id) === String(nodeId)) {
            return node.title;
        }
        const childTitle = findScormNodeTitle(node.children || [], nodeId);
        if (childTitle) {
            return childTitle;
        }
    }
    return null;
};

function ScormProgressSummary({ launchState, runtimeState, saveState }) {
    const state = runtimeState || buildRuntimeStateFromLaunch(launchState);
    const percent = Number(state?.completionPercent || 0);
    const status = state?.successStatus || state?.completionStatus || state?.lessonStatus || state?.registrationStatus || "en cours";
    const score = state?.scoreScaled != null
        ? `${Math.round(Number(state.scoreScaled) * 100)}%`
        : state?.scoreRaw != null ? String(state.scoreRaw) : null;
    const time = formatDuration(state?.totalTimeSeconds);
    const saveLabel = saveState === "saving"
        ? "Sauvegarde..."
        : saveState === "saved"
            ? "Progression sauvegardée"
            : saveState === "error"
                ? "Sauvegarde a vérifier"
                : launchState?.resume ? "Reprise automatique" : null;

    return (
        <div className="scorm-progress-summary">
            <span className="scorm-progress-summary__bar" aria-label="Progression SCORM">
                <span style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
            </span>
            <span>{percent}%</span>
            <span>{status}</span>
            {score ? <span>Score {score}</span> : null}
            {time ? <span>{time}</span> : null}
            {saveLabel ? <strong>{saveLabel}</strong> : null}
        </div>
    );
}

const formatDuration = (seconds) => {
    const value = Number(seconds || 0);
    if (!value) return null;
    const minutes = Math.floor(value / 60);
    const remainder = value % 60;
    if (minutes < 60) return `${minutes} min ${remainder}s`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};
